import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  Settings, 
  LayoutDashboard,
  Briefcase,
  Plus,
  Search,
  UserPlus
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_videocrew-ct/artifacts/ipgpa8tb_VGCT_LOGO.png";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [myProfilePath, setMyProfilePath] = useState("/dashboard?tab=profile");

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/profiles/user/${user.user_id}`)
      .then((r) => setMyProfilePath(`/profile/${r.data.slug || r.data.profile_id}`))
      .catch(() => setMyProfilePath("/dashboard?tab=profile"));
  }, [user, location.pathname]);

  const navLinks = [
    { href: "/browse", label: "FIND CREATIVES" },
    { href: "/jobs", label: "JOBS" },
    { href: "/categories", label: "CATEGORIES" },
    { href: "/advertise", label: "ADVERTISE" },
    { href: "/contact", label: "CONTACT" },
    { href: "/about", label: "ABOUT US" },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0 mr-6" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="Videography Cape Town" 
              className="h-10 w-auto"
            />
            <span className="hidden sm:block font-heading text-lg tracking-tight">
              VIDEOGRAPHY<span className="text-orange-500">CT</span>
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-5">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-mono tracking-wider whitespace-nowrap transition-colors ${
                  isActive(link.href)
                    ? "text-orange-500"
                    : "text-zinc-400 hover:text-white"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-4">
            {/* Search Icon */}
            <Button
              variant="ghost"
              size="icon"
              className="text-zinc-400 hover:text-white"
              onClick={() => navigate("/browse")}
              data-testid="nav-search-btn"
            >
              <Search className="h-5 w-5" />
            </Button>

            {user ? (
              <>
                <Button
                  onClick={() => navigate(myProfilePath)}
                  variant="outline"
                  className="hidden md:flex items-center gap-2 border-orange-600 text-orange-500 hover:bg-orange-600 hover:text-white"
                  data-testid="nav-my-profile-btn"
                >
                  <User className="h-4 w-4" />
                  MY PROFILE
                </Button>

                {/* Post Job Button */}
                <Button
                  onClick={() => navigate("/post-job")}
                  className="hidden md:flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white"
                  data-testid="nav-post-job-btn"
                >
                  <Plus className="h-4 w-4" />
                  POST JOB
                </Button>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="relative h-9 w-9 rounded-full p-0 overflow-hidden border border-zinc-700"
                      data-testid="user-menu-trigger"
                    >
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <User className="h-5 w-5 text-zinc-400" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-56 bg-zinc-900 border-zinc-800"
                  >
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem
                      onClick={() => navigate("/dashboard")}
                      className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                      data-testid="menu-dashboard"
                    >
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/dashboard?tab=profile")}
                      className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                      data-testid="menu-profile"
                    >
                      <User className="mr-2 h-4 w-4" />
                      My Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/dashboard?tab=jobs")}
                      className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                      data-testid="menu-my-jobs"
                    >
                      <Briefcase className="mr-2 h-4 w-4" />
                      My Jobs
                    </DropdownMenuItem>
                    {user.user_type === "advertiser" && (
                      <DropdownMenuItem
                        onClick={() => navigate("/advertiser")}
                        className="cursor-pointer text-orange-400 focus:text-orange-300 focus:bg-zinc-800"
                        data-testid="menu-advertiser"
                      >
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Advertiser Dashboard
                      </DropdownMenuItem>
                    )}
                    {user.is_admin && (
                      <>
                        <DropdownMenuSeparator className="bg-zinc-800" />
                        <DropdownMenuItem
                          onClick={() => navigate("/admin")}
                          className="cursor-pointer text-zinc-300 focus:text-white focus:bg-zinc-800"
                          data-testid="menu-admin"
                        >
                          <Settings className="mr-2 h-4 w-4" />
                          Admin Dashboard
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-zinc-800" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer text-red-400 focus:text-red-300 focus:bg-zinc-800"
                      data-testid="menu-logout"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  className="text-zinc-300 hover:text-white"
                  data-testid="nav-login-btn"
                >
                  LOG IN
                </Button>
                <Button
                  onClick={() => navigate("/signup")}
                  className="bg-orange-600 hover:bg-orange-500 text-white"
                  data-testid="nav-signup-btn"
                >
                  SIGN UP
                </Button>
                <Button
                  onClick={() => navigate("/signup")}
                  className="bg-white text-zinc-950 hover:bg-orange-500 hover:text-white font-semibold"
                  data-testid="nav-create-profile-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  CREATE YOUR PROFILE
                </Button>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-80 bg-zinc-950 border-zinc-800 p-0"
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                    <span className="font-heading text-lg">MENU</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  
                  <div className="flex-1 overflow-auto py-4">
                    {navLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block px-6 py-3 text-sm font-mono tracking-widest transition-colors ${
                          isActive(link.href)
                            ? "text-orange-500 bg-zinc-900"
                            : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                    
                    {user && (
                      <>
                        <div className="border-t border-zinc-800 my-4" />
                        <Link
                          to={myProfilePath}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-6 py-3 text-sm font-mono tracking-widest text-orange-500 hover:bg-zinc-900"
                          data-testid="mobile-my-profile-link"
                        >
                          MY PROFILE
                        </Link>
                        <Link
                          to="/dashboard"
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-6 py-3 text-sm font-mono tracking-widest text-zinc-400 hover:text-white hover:bg-zinc-900"
                        >
                          DASHBOARD
                        </Link>
                        <Link
                          to="/post-job"
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-6 py-3 text-sm font-mono tracking-widest text-zinc-400 hover:text-white hover:bg-zinc-900"
                        >
                          POST A JOB
                        </Link>
                        {user.is_admin && (
                          <Link
                            to="/admin"
                            onClick={() => setMobileMenuOpen(false)}
                            className="block px-6 py-3 text-sm font-mono tracking-widest text-zinc-400 hover:text-white hover:bg-zinc-900"
                          >
                            ADMIN
                          </Link>
                        )}
                      </>
                    )}
                  </div>
                  
                  <div className="p-4 border-t border-zinc-800">
                    {user ? (
                      <Button
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        variant="outline"
                        className="w-full border-zinc-700 text-zinc-300"
                      >
                        LOG OUT
                      </Button>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => {
                            navigate("/login");
                            setMobileMenuOpen(false);
                          }}
                          variant="outline"
                          className="w-full border-zinc-700 text-zinc-300"
                          data-testid="mobile-login-btn"
                        >
                          LOG IN
                        </Button>
                        <Button
                          onClick={() => {
                            navigate("/signup");
                            setMobileMenuOpen(false);
                          }}
                          className="w-full bg-orange-600 hover:bg-orange-500"
                          data-testid="mobile-signup-btn"
                        >
                          SIGN UP
                        </Button>
                        <Button
                          onClick={() => {
                            navigate("/signup");
                            setMobileMenuOpen(false);
                          }}
                          className="w-full bg-white text-zinc-950 hover:bg-orange-500 hover:text-white font-semibold"
                          data-testid="mobile-create-profile-btn"
                        >
                          CREATE YOUR PROFILE
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
